import React, { useEffect, useState } from "react";
import axios from "axios";

const HomepageVideoEditor = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    homepage_video_link: [""],
    bannerBottomStatus: 0,
  });

  const fetchHomepageVideo = async () => {
    try {
      setLoading(true);
      setMessage("");
      setError("");

      const res = await axios.get(
        "https://eventscons.com/eventbackendnew/api/homepage-get-video-link"
      );

      const apiData = res?.data?.data;
      const links =
        Array.isArray(apiData?.homepage_video_link) &&
        apiData.homepage_video_link.length > 0
          ? apiData.homepage_video_link
          : [""];

      setForm({
        homepage_video_link: links,
        bannerBottomStatus: apiData?.bannerBottomStatus ? 1 : 0,
      });
    } catch (err) {
      setForm((prev) => ({
        ...prev,
        homepage_video_link:
          prev.homepage_video_link?.length > 0 ? prev.homepage_video_link : [""],
      }));
      setError(
        err?.response?.data?.message || "Failed to load homepage video data"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHomepageVideo();
  }, []);

  const addVideoLink = () => {
    setForm((prev) => ({
      ...prev,
      homepage_video_link: [...prev.homepage_video_link, ""],
    }));
  };

  const removeVideoLink = (index) => {
    if (form.homepage_video_link.length === 1) {
      const updated = [""];
      setForm((prev) => ({
        ...prev,
        homepage_video_link: updated,
      }));
      return;
    }

    const updated = form.homepage_video_link.filter((_, i) => i !== index);

    setForm((prev) => ({
      ...prev,
      homepage_video_link: updated.length ? updated : [""],
    }));
  };

  const handleVideoLinkChange = (index, value) => {
    const updated = [...form.homepage_video_link];
    updated[index] = value;

    setForm((prev) => ({
      ...prev,
      homepage_video_link: updated,
    }));
  };

  const handleBannerToggle = () => {
    setForm((prev) => ({
      ...prev,
      bannerBottomStatus: prev.bannerBottomStatus === 1 ? 0 : 1,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setMessage("");
      setError("");

      const filteredLinks = form.homepage_video_link
        .map((link) => link.trim())
        .filter((link) => link !== "");

      if (filteredLinks.length === 0) {
        setError("Please enter at least one video link");
        setSaving(false);
        return;
      }

      const formData = new FormData();

      filteredLinks.forEach((link) => {
        formData.append("homepage_video_link[]", link);
      });

      formData.append("bannerBottomStatus", form.bannerBottomStatus);

      const res = await axios.post(
        "https://eventscons.com/eventbackendnew/api/homepage-upload-video",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setMessage(res?.data?.message || "Homepage video updated successfully");
      fetchHomepageVideo();
    } catch (err) {
      setError(err?.response?.data?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section style={styles.section}>
      <div style={styles.container}>
        {message && <p style={styles.success}>{message}</p>}
        {error && <p style={styles.error}>{error}</p>}

        <div style={styles.card}>
          <div style={styles.form}>
            {loading ? (
              <p style={styles.loading}>Loading...</p>
            ) : (
              <form onSubmit={handleSubmit}>
                <div style={styles.fieldGroup}>
                  <div style={styles.labelRow}>
                    <label style={styles.label}>Homepage Video Links</label>

                    <button
                      type="button"
                      onClick={addVideoLink}
                      style={styles.addCircleBtn}
                      title="Add more video link"
                    >
                      +
                    </button>
                  </div>

                  <div style={styles.videoLinksContainer}>
                    {form.homepage_video_link.map((link, index) => (
                      <div key={index} style={styles.videoLinkRow}>
                        <input
                          type="url"
                          value={link}
                          onChange={(e) =>
                            handleVideoLinkChange(index, e.target.value)
                          }
                          style={styles.input}
                          placeholder={`Video link ${index + 1} (https://...)`}
                        />

                        <button
                          type="button"
                          onClick={() => removeVideoLink(index)}
                          style={{
                            ...styles.removeBtn,
                            opacity:
                              form.homepage_video_link.length === 1 ? 0.7 : 1,
                          }}
                          title="Remove video link"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Banner Bottom Status</label>

                  <div style={styles.toggleWrap}>
                    <button
                      type="button"
                      onClick={handleBannerToggle}
                      style={{
                        ...styles.toggleButton,
                        justifyContent:
                          form.bannerBottomStatus === 1
                            ? "flex-end"
                            : "flex-start",
                        background:
                          form.bannerBottomStatus === 1 ? "#22c55e" : "#d1d5db",
                      }}
                      aria-label="Toggle banner bottom status"
                    >
                      <span style={styles.toggleKnob}></span>
                    </button>

                    <span
                      style={{
                        ...styles.toggleText,
                        color:
                          form.bannerBottomStatus === 1 ? "#16a34a" : "#6b7280",
                      }}
                    >
                      {form.bannerBottomStatus === 1 ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                </div>

                <button type="submit" style={styles.button} disabled={saving}>
                  {saving ? "Saving..." : "Save Homepage Video"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: {
    padding: "40px 20px",
    border: "1px solid #eee",
    background: "#fff",
    margin: "30px",
    boxShadow: "0px 10px 25px #dcdcdc",
    borderRadius: "15px",
  },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
  },
  card: {
    background: "#fff",
    borderRadius: "14px",
    border: "1px solid #efefef",
    padding: "16px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },
  videoLinksContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  videoLinkRow: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    fontWeight: "600",
    color: "#333",
    fontSize: "15px",
  },
  input: {
    padding: "12px 14px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "15px",
    flex: 1,
    boxSizing: "border-box",
    outline: "none",
  },
  addCircleBtn: {
    width: "38px",
    height: "38px",
    borderRadius: "50%",
    background: "#10b981",
    color: "#fff",
    border: "none",
    fontSize: "24px",
    fontWeight: "700",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    boxShadow: "0 6px 16px rgba(16, 185, 129, 0.25)",
  },
  removeBtn: {
    padding: "12px 14px",
    background: "#ef4444",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "18px",
    fontWeight: "bold",
    cursor: "pointer",
    width: "48px",
    height: "48px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  toggleWrap: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  toggleButton: {
    width: "64px",
    height: "34px",
    borderRadius: "999px",
    border: "none",
    display: "flex",
    alignItems: "center",
    padding: "4px",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  toggleKnob: {
    width: "26px",
    height: "26px",
    borderRadius: "50%",
    background: "#fff",
    boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
  },
  toggleText: {
    fontSize: "16px",
    fontWeight: "600",
  },
  button: {
    padding: "14px 20px",
    background: "#9B1321",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    marginTop: "10px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    width: "fit-content",
  },
  success: {
    color: "green",
    fontWeight: "600",
    marginBottom: "14px",
  },
  error: {
    color: "red",
    fontWeight: "600",
    marginBottom: "14px",
  },
  loading: {
    textAlign: "center",
    padding: "20px 0",
  },
};

export default HomepageVideoEditor;